import { useState, type ReactNode } from "react";
import { Copy, Download, Ellipsis, FolderInput, Link2, Pencil, RotateCcw, Share2, Trash2, X, type LucideIcon } from "@fg/icons";
import type { DriveItem } from "@/api/driveApi";
import { Menu, anchorFrom, type MenuAnchor } from "@/components/ui/menu";
import { cn } from "@/lib/utils";
import { useDriveActions } from "./driveActions";
import { buildItemMenu, canDownload, canEdit, owns } from "./items/menuItems";
import { cmdClass } from "./items/ViewControls";
import type { DriveVariant } from "./types";

/**
 * Fluent command bar above the files card: Share, Copy link, Download ·
 * Move to, Copy to, Rename · Delete, then ⋯ (the full item menu) for the
 * selection — each enabled only when the user's access allows it. The right
 * side carries "N selected", sort, layout and the details toggle.
 */
export function CommandBar({
    items,
    variant,
    list,
    zipParentId,
    onRename,
    onDetails,
    onComment,
    onClear,
    trailing
}: {
    /** The selected items. */
    items: DriveItem[];
    variant: DriveVariant;
    /** Everything visible (preview steps through it). */
    list: DriveItem[];
    zipParentId: string | null;
    /** Start inline rename (F2). */
    onRename: (item: DriveItem) => void;
    onDetails: (item: DriveItem) => void;
    onComment: (item: DriveItem) => void;
    onClear: () => void;
    trailing: ReactNode;
}) {
    const a = useDriveActions();
    const [more, setMore] = useState<{ anchor: MenuAnchor; keyboard: boolean } | null>(null);
    const n = items.length;
    const single = n === 1 ? items[0] : undefined;
    const all = (test: (i: DriveItem) => boolean) => n > 0 && items.every(test);

    const cmd = (label: string, Icon: LucideIcon, enabled: boolean, onClick: () => void, danger?: boolean) => (
        <button key={label} type="button" disabled={!enabled} onClick={onClick} title={label} aria-label={label} className={cmdClass}>
            <Icon className={cn("h-4 w-4", danger && "text-[var(--drive-danger)]")} aria-hidden />
            <span className="hidden text-[13px] md:inline">{label}</span>
        </button>
    );
    const sep = (key: string) => <span key={key} className="mx-1.5 h-5 w-px shrink-0 bg-[var(--drive-line)]" aria-hidden />;

    return (
        <div
            role="toolbar"
            aria-label="Commands"
            data-no-clear=""
            className="drive-no-scrollbar flex h-11 shrink-0 items-center gap-0.5 overflow-x-auto border-b border-[var(--drive-line)] bg-card px-2.5"
        >
            {variant === "trash" ? (
                <>
                    {cmd("Restore", RotateCcw, n > 0, () => void a.restore(items))}
                    {cmd("Delete forever", Trash2, n > 0, () => void a.deleteForever(items), true)}
                </>
            ) : (
                <>
                    {cmd("Share", Share2, Boolean(single && canEdit(single)), () => single && a.share(single))}
                    {cmd("Copy link", Link2, Boolean(single && canEdit(single)), () => single && void a.copyLink(single))}
                    {cmd("Download", Download, all(canDownload), () => void a.download(items))}
                    {sep("s1")}
                    {cmd("Move to", FolderInput, all(owns), () => void a.move(items))}
                    {cmd("Copy to", Copy, all((i) => i.kind === "File" && canDownload(i)), () => void a.copyTo(items))}
                    {cmd("Rename", Pencil, Boolean(single && canEdit(single)), () => single && onRename(single))}
                    {sep("s2")}
                    {cmd("Delete", Trash2, all(owns), () => void a.trash(items), true)}
                </>
            )}
            <button
                type="button"
                disabled={n === 0}
                aria-haspopup="menu"
                aria-expanded={Boolean(more)}
                aria-label="More actions for the selection"
                title="More actions"
                onClick={(e) => setMore({ anchor: anchorFrom(e.currentTarget), keyboard: e.detail === 0 })}
                className={cn(cmdClass, "w-8 justify-center px-0")}
            >
                <Ellipsis className="h-4 w-4" aria-hidden />
            </button>

            <span className="min-w-2 flex-1" />

            <span aria-live="polite" className="shrink-0 whitespace-nowrap px-1 text-[13px] text-[var(--drive-text-2)]">
                {n > 0 ? `${n} selected` : ""}
            </span>
            {n > 0 ? (
                <>
                    <button
                        type="button"
                        onClick={onClear}
                        aria-label="Clear selection"
                        title="Clear selection (Esc)"
                        className={cn(cmdClass, "w-7 justify-center px-0 text-[var(--drive-text-2)]")}
                    >
                        <X className="h-4 w-4" aria-hidden />
                    </button>
                    {sep("s3")}
                </>
            ) : null}
            {trailing}

            {more && n > 0 ? (
                <Menu
                    label="Selection actions"
                    anchor={more.anchor}
                    focusFirst={more.keyboard}
                    onClose={() => setMore(null)}
                    entries={buildItemMenu(items, { variant, actions: a, list, onDetails, onComment, zipParentId })}
                />
            ) : null}
        </div>
    );
}
